/**
 * admin.mahi.fashion — global auth context.
 * Provides { admin, loading, refresh, signOut } to every page.
 *
 * Unlike MAHI's consumer auth.js, this waits for /auth/me before rendering
 * the authenticated shell (no optimistic cached-user render): the role on
 * that response gates which nav items and actions are even shown, and
 * showing the wrong ones for a moment on a panel that can expose user PII is
 * the wrong tradeoff for internal tooling, where a few hundred ms of spinner
 * costs nothing a consumer app's snappiness would.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import adminApi from './api';

const Ctx = createContext({ admin: null, loading: true, refresh: () => {}, signOut: () => {} });

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('admin_mahi_token')) {
      setAdmin(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await adminApi.get('/auth/me');
      setAdmin(data.admin);
      return data.admin;
    } catch {
      // 401 → api.js interceptor clears the token and redirects to /login.
      setAdmin(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('admin_mahi_token');
    setAdmin(null);
    window.location.href = '/login';
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return <Ctx.Provider value={{ admin, loading, refresh, signOut }}>{children}</Ctx.Provider>;
};

export const useAdminAuth = () => useContext(Ctx);

/** True if `admin`'s role is in `roles`. Used to gate nav items and actions. */
export const hasRole = (admin, ...roles) => Boolean(admin && roles.includes(admin.role));
